import React, { useCallback, useMemo, useState } from 'react';
import { Hash, FileText, Quote, Sparkles, AlertTriangle, Plus, Search, GripVertical, Pencil, Flag, Check } from 'lucide-react';
import type {
  CanvasPrimitive,
  DrawingFigureFull,
  DrawingFigureSummary,
  DrawingNumeralRow,
  DrawingTermMappingRow,
  NumeralCreateBody,
  NumeralPatchBody,
} from '@/api/client';
import {
  createDrawingNumeral,
  deleteDrawingNumeral,
  patchDrawingNumeral,
} from '@/api/client';
import { useAppStore } from '@/store';
import { useDrawingStore, type ReferencePanelTab } from '@/store/drawingStore';
import { isBindableClaimTerm } from './claimTerms';

/** Slice 6G.2 — drag/drop transfer key. The Canvas surface listens
 * for this on `dataTransfer.types` and creates a text primitive at
 * the drop point if found. */
export const DRAWING_TERM_DRAG_TYPE = 'application/x-drawing-term';

/** PD-005 — drag/drop transfer key for registry-numeral chips.
 * The Canvas surface listens for this in parallel with the term
 * drag type and creates a NUMERAL primitive (bound to the registry
 * row via drawing_numeral_id) at the drop point. Distinct MIME
 * keeps it from colliding with the term-glossary drag. */
export const DRAWING_NUMERAL_DRAG_TYPE = 'application/x-drawing-numeral';

export interface DraggedTermPayload {
  /** Display string — becomes the text primitive's `text`. */
  name: string;
  /** Optional source claim id, when the term came from a specific
   * claim's analysis. */
  source_claim_id?: string;
  /** BUG-021 — every claim_number this term appears in, surfaced
   * so the panel can render "From Claim 1" / "From Claims 1, 3"
   * instead of a truncated UUID. */
  source_claim_numbers?: number[];
}

export interface DraggedNumeralPayload {
  /** ``drawing_numerals.id`` — the registry-row id the placed
   * primitive will bind to. The Canvas drop handler stores this
   * in ``primitive.drawing_numeral_id`` so the chip stays in
   * sync with the registry row's label / claim binding via the
   * standard ``numerals.find(n => n.id === ...)`` lookup. */
  numeralId: string;
  /** Numeric / alphanumeric token shown on the chip (e.g. "100",
   * "112a"). Cached on the primitive's ``primitive_data_json``
   * for the fallback path when the registry row is deleted. */
  numeral: string;
  /** Display label for the registry row. Kept as a fallback on
   * the primitive so an orphaned chip still renders something
   * meaningful. */
  label: string;
}

interface Props {
  /** CHUNK 2 — needed by the simplified Numerals tab so the inline
   * edit panel can call create/patch/delete without prop-drilling
   * through the tab boundary. */
  matterId: string;
  numerals: DrawingNumeralRow[];
  termMappings: DrawingTermMappingRow[];
  activeTab: ReferencePanelTab;
  onTabChange: (tab: ReferencePanelTab) => void;
  /** Active figure summary — drives the Phase 5E claim-prime banner.
   * Optional so older callers (and the no-figure-selected state)
   * still work. */
  activeFigure?: DrawingFigureSummary | DrawingFigureFull | null;
  /** Slice 5F — fired when the drafter clicks Retry on a failed
   * priming. Renders the button as disabled if absent. */
  onRetryPriming?: (figureId: string) => void;
  /** Slice 5F — true while the reprime call for the active figure is
   * in flight (drives the spinner on the Retry button). */
  reprimePending?: boolean;
  /** Slice 6G — full figures list so the Numerals tab can render
   * "FIG. N (HERE) · FIG. M" placement footprints. */
  figures?: DrawingFigureSummary[];
  /** Slice 6G — per-figure canvas primitives keyed by figure id, so
   * the panel can compute which numerals are placed where. */
  canvasPrimitives?: Record<string, CanvasPrimitive[]>;
}

interface ClaimPrimeJson {
  visible_numerals?: string[];
  candidate_claim_elements?: Array<{
    claim_id?: string;
    element_name?: string;
    confidence?: 'high' | 'medium' | 'low' | string;
  }>;
  uncertainties?: string[];
  prime_confidence?: string;
  description_source?: string;
  base_description?: string;
}

function _readPrime(figure?: Props['activeFigure']): ClaimPrimeJson | null {
  if (!figure) return null;
  const raw = (figure as DrawingFigureFull).claim_prime_json;
  if (!raw || typeof raw !== 'object') return null;
  return raw as ClaimPrimeJson;
}

/**
 * Right-panel reference (Phase 4D, read-only).
 *
 * Three tabs: Numerals · Claims · Terms. Each carries a count badge
 * derived from the data already in the store. The active tab is
 * sticky (persisted to localStorage by the store's
 * `setReferencePanelTab`). Empty states copy hints at where the data
 * lights up — Numerals + Terms come live in Phase 5/6 when figures
 * land; the Claims tab gets populated from `claim_prime_json` in
 * Phase 5 and rollup mappings in Phase 6.
 */
export default function ReferencePanel({
  matterId, numerals, termMappings, activeTab, onTabChange, activeFigure,
  onRetryPriming, reprimePending, figures = [], canvasPrimitives = {},
}: Props) {
  const prime = _readPrime(activeFigure);
  const candidates = prime?.candidate_claim_elements || [];
  // Claims-tab badge — shows total claim count when frozen
  // claims are loaded. The previous expression rendered the
  // count of distinct claims appearing in termMappings (i.e.
  // the count of claims with at least one mapping row), which
  // for a matter with 12 frozen claims + 3 mapped would render
  // as a misleading "3" badge despite the tab displaying 12
  // cards. Falling back to the priming-only count keeps the
  // pre-mappings preview shape intact.
  const claimsList = useAppStore((s) => s.matterState?.claims);
  const claimsCount = (claimsList && claimsList.length > 0)
    ? claimsList.length
    : (candidates.length > 0
      ? new Set(candidates.map((c) => c?.claim_id || '')).size
      : countDistinctClaims(termMappings));
  // The Numerals tab folds in claim_prime_json.visible_numerals when
  // the figure has been primed but no canvas-state numerals exist
  // yet (Phase 6 lands placement).
  const visibleNumerals = (prime?.visible_numerals || []).filter(
    (n) => typeof n === 'string' && n.length > 0,
  );
  // BUG-030 — tab badge reflects the figure-actionable count
  // (Group A + Group B). When no figure is selected, fall back
  // to the matter-wide count so the badge still surfaces the
  // registry size. Uses the registry's ``figure_ids`` join (the
  // canonical server-side source) instead of walking the
  // partially-hydrated canvasPrimitives cache.
  const activeFigureId = (activeFigure as { id?: string } | null | undefined)?.id || null;
  const numeralsBadgeCount = (() => {
    if (!activeFigureId) {
      return numerals.length + (numerals.length === 0 ? visibleNumerals.length : 0);
    }
    let placedHereOrUnplaced = 0;
    for (const n of numerals) {
      const ids = Array.isArray(n.figure_ids) ? n.figure_ids : [];
      if (ids.length === 0) placedHereOrUnplaced += 1;
      else if (ids.includes(activeFigureId)) placedHereOrUnplaced += 1;
    }
    return placedHereOrUnplaced + (numerals.length === 0 ? visibleNumerals.length : 0);
  })();
  // Prime banner state — three modes: primed-ok, prime-failed, idle.
  const primeError = (activeFigure as DrawingFigureFull | undefined)?.prime_error;
  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="reference-panel">
      <PrimeBanner
        figure={activeFigure}
        prime={prime}
        primeError={primeError || null}
        onRetry={onRetryPriming}
        retryPending={!!reprimePending}
      />

      <div
        role="tablist"
        aria-label="Reference panel"
        className="flex border-b border-zinc-200 bg-white"
      >
        <Tab
          tabId="numerals"
          label="Numerals"
          count={numeralsBadgeCount}
          icon={<Hash className="h-3.5 w-3.5" />}
          active={activeTab === 'numerals'}
          onClick={() => onTabChange('numerals')}
        />
        <Tab
          tabId="claims"
          label="Claims"
          count={claimsCount}
          icon={<FileText className="h-3.5 w-3.5" />}
          active={activeTab === 'claims'}
          onClick={() => onTabChange('claims')}
        />
        <Tab
          tabId="terms"
          label="Terms"
          count={termMappings.length}
          icon={<Quote className="h-3.5 w-3.5" />}
          active={activeTab === 'terms'}
          onClick={() => onTabChange('terms')}
        />
      </div>

      <div className="flex-1 overflow-y-auto" role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
        {activeTab === 'numerals' && (
          <NumeralsTab
            matterId={matterId}
            numerals={numerals}
            termMappings={termMappings}
            primedNumerals={visibleNumerals}
            figures={figures}
            canvasPrimitives={canvasPrimitives}
            activeFigureId={activeFigureId}
            activeFigureFrozen={!!(activeFigure as DrawingFigureSummary | undefined)?.is_frozen}
          />
        )}
        {activeTab === 'claims' && (
          <ClaimsTab
            matterId={matterId}
            termMappings={termMappings}
            numerals={numerals}
            primeCandidates={candidates}
          />
        )}
        {activeTab === 'terms' && (
          <TermsTab
            termMappings={termMappings}
            numerals={numerals}
            canvasPrimitives={canvasPrimitives}
            activeFigureId={activeFigureId}
          />
        )}
      </div>
    </div>
  );
}

interface PrimeBannerProps {
  figure: Props['activeFigure'];
  prime: ClaimPrimeJson | null;
  primeError: string | null;
  onRetry?: (figureId: string) => void;
  retryPending?: boolean;
}

function PrimeBanner({ figure, prime, primeError, onRetry, retryPending }: PrimeBannerProps) {
  if (!figure) return null;
  if (primeError && !prime) {
    const figureId = (figure as { id?: string }).id;
    const canRetry = !!onRetry && !!figureId;
    return (
      <div
        className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        data-testid="prime-banner-failed"
        role="status"
      >
        <AlertTriangle className="mt-[1px] h-3.5 w-3.5 flex-shrink-0" aria-hidden />
        <div className="flex-1">
          <div className="font-medium">Priming failed</div>
          <p className="mt-0.5 text-[11px]">{primeError}</p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-white px-2 py-1 text-[11px] font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!canRetry || retryPending}
          onClick={() => { if (canRetry && figureId) onRetry!(figureId); }}
          title={
            canRetry
              ? 'Re-run drawing_figure_claim_prime for this figure.'
              : 'Open this figure to re-prime it.'
          }
          data-testid="prime-retry"
        >
          {retryPending ? 'Retrying…' : 'Retry priming'}
        </button>
      </div>
    );
  }
  if (!prime) return null;
  const candidateCount = prime.candidate_claim_elements?.length || 0;
  const uncertaintyCount = prime.uncertainties?.length || 0;
  const confidence = prime.prime_confidence;
  return (
    <div
      className="flex items-start gap-2 border-b border-[#002FA7]/20 bg-[#002FA7]/5 px-3 py-2 text-xs text-[#002FA7]"
      data-testid="prime-banner"
      role="status"
    >
      <Sparkles className="mt-[1px] h-3.5 w-3.5 flex-shrink-0" aria-hidden />
      <div className="flex-1">
        <div className="font-medium">
          Primed
          {confidence ? ` · ${confidence} confidence` : ''}
          {' · '}
          {candidateCount} claim element{candidateCount === 1 ? '' : 's'}
          {uncertaintyCount > 0 && ` · ${uncertaintyCount} uncertaint${uncertaintyCount === 1 ? 'y' : 'ies'}`}
        </div>
        {prime.description_source && (
          <p className="mt-0.5 text-[11px] text-[#002FA7]/80">
            description source: {prime.description_source}
          </p>
        )}
      </div>
    </div>
  );
}

function countDistinctClaims(rows: DrawingTermMappingRow[]): number {
  const set = new Set<string>();
  for (const r of rows) set.add(r.claim_id);
  return set.size;
}

interface TabProps {
  tabId: ReferencePanelTab;
  label: string;
  count: number;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}

function Tab({ tabId, label, count, icon, active, onClick }: TabProps) {
  return (
    <button
      type="button"
      role="tab"
      id={`tab-${tabId}`}
      aria-selected={active}
      aria-controls={`tabpanel-${tabId}`}
      data-testid={`tab-${tabId}`}
      data-active={active ? 'true' : 'false'}
      className={[
        'flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium',
        'transition-colors',
        active
          ? 'border-b-2 border-[#002FA7] text-[#002FA7]'
          : 'border-b-2 border-transparent text-zinc-500 hover:text-zinc-700',
      ].join(' ')}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
      {count > 0 && (
        <span
          className={[
            'rounded-sm px-1.5 py-0.5 text-[10px] font-semibold',
            active ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'bg-zinc-100 text-zinc-500',
          ].join(' ')}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Numerals tab — simplified flat list (CHUNK 2) ──────────────
//
// The canvas-level numeral-placement workflow has been removed.
// What stays:
//   - The drawing_numerals registry table (matter-scoped).
//   - The Phase 8 auto-mapping contract (numeral_to_claim_map in
//     analyze_bundle_json — the spec module's locked input).
//   - The NumeralChip SVG renderer (existing chips on prior
//     matters render unchanged).
// What's gone:
//   - "place →" pill button (the canvas chip-arming affordance).
//   - The new-vs-reuse modal forking off canvas clicks.
//   - The "+ New" → drop-at-canvas-centre flow.
// New numerals from this UI have no canvas-coordinate footprint;
// the matter's analyze_bundle / spec output is unaffected because
// coordinates were never in the locked subset of bundle keys.

function NumeralsTab({
  matterId, numerals, termMappings, primedNumerals = [],
  figures, canvasPrimitives, activeFigureId, activeFigureFrozen,
}: {
  matterId: string;
  numerals: DrawingNumeralRow[];
  /** Drives the three-state numeral badge: any numeral.id that
   *  appears as ``drawing_numeral_id`` on a term-mapping row gets
   *  the "mapped" badge, distinct from "linked" (bound_claim_id
   *  set) and "unmapped" (neither). Auto-map writes
   *  drawing_term_mappings.drawing_numeral_id but only writes
   *  bound_claim_id for high-confidence resolutions — without
   *  this signal medium/low auto-map outcomes would render as
   *  "unmapped" even though the term-mapping link exists. */
  termMappings: DrawingTermMappingRow[];
  primedNumerals?: string[];
  figures: DrawingFigureSummary[];
  canvasPrimitives: Record<string, CanvasPrimitive[]>;
  activeFigureId: string | null;
  /** BUG-035 — when the active figure is frozen the canvas
   * silently swallows placement clicks. Disable the "place →"
   * pill so drafters don't end up in the armed state without a
   * way to commit. The figure-row Unfreeze button is the
   * intended remedial path. */
  activeFigureFrozen: boolean;
}) {
  // BUG-030 — compute the figure-scoped grouping. ``placementMap``
  // maps each registry numeral id → the set of figure ids that
  // already carry a chip linked to it. Built from the registry's
  // ``figure_ids`` field (server-side join over
  // drawing_canvas_state) — which means we don't need any
  // canvas-state to be hydrated client-side, and we see placements
  // on figures the drafter hasn't opened yet. ``canvasPrimitives``
  // is folded in additively so chips placed in the current session
  // (not yet persisted) still count for the active figure.
  const placementMap = useMemo<Map<string, Set<string>>>(() => {
    const out = new Map<string, Set<string>>();
    for (const n of numerals) {
      const ids = Array.isArray(n.figure_ids) ? n.figure_ids : [];
      if (ids.length === 0) continue;
      out.set(n.id, new Set(ids));
    }
    // Local, not-yet-flushed canvas primitives — fold in so a
    // freshly-placed chip moves the registry row from Group B to
    // Group A immediately, without waiting for the next refreshLists
    // to repopulate ``figure_ids``.
    for (const [figureId, prims] of Object.entries(canvasPrimitives)) {
      for (const p of prims) {
        if (p.primitive_type !== 'numeral') continue;
        const numId = p.drawing_numeral_id;
        if (!numId) continue;
        let set = out.get(numId);
        if (!set) { set = new Set(); out.set(numId, set); }
        set.add(figureId);
      }
    }
    return out;
  }, [numerals, canvasPrimitives]);
  // Auto-map status: any numeral id that appears as
  // drawing_numeral_id on a term-mapping row counts as "mapped"
  // even when bound_claim_id hasn't been written (medium / low
  // confidence resolutions stay drafter-confirmable). Drives the
  // 'linked' / 'mapped' / 'unmapped' three-state badge below.
  const mappedNumeralIds = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    for (const t of termMappings) {
      if (t.drawing_numeral_id) {
        s.add(t.drawing_numeral_id);
      }
    }
    return s;
  }, [termMappings]);
  // Phase 3 — startNumeralPlacement / pendingNumeralId reads
  // retired with the # canvas tool. The "place →" pill itself
  // came out in Phase 1; ``armedNumeralId`` is now always null
  // for the NumeralGroup/NumeralRow callers that still take it
  // as a prop. Keeping the prop keeps the row component shape
  // unchanged for legacy consumers.
  const armedNumeralId: string | null = null;
  const claims = useAppStore((s) => s.matterState?.claims) || [];
  // Glossary of available claim terms — pulled from each claim's
  // analysis.key_terms, keeping only terms its current claim_text
  // still recites (key_terms go stale after a steer). `(claim_id,
  // claim_term)` is the shape Phase 8 auto-mapping writes into
  // drawing_numerals; the edit panel writes the same pair.
  const claimTermOptions = useMemo<{
    claimId: string; claimNumber: number; term: string;
  }[]>(() => {
    const out: { claimId: string; claimNumber: number; term: string }[] = [];
    const seen = new Set<string>();
    const ordered = [...claims].sort((a, b) => a.claim_number - b.claim_number);
    for (const c of ordered) {
      const terms = c?.analysis?.key_terms || [];
      for (const t of terms) {
        if (typeof t !== 'string') continue;
        const trimmed = t.trim();
        if (!trimmed) continue;
        if (!isBindableClaimTerm(trimmed, c.claim_text)) continue;
        const key = `${c.id}|${trimmed.toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          claimId: c.id,
          claimNumber: c.claim_number,
          term: trimmed,
        });
      }
    }
    return out;
  }, [claims]);

  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const refreshLists = useDrawingStore((s) => s.refreshLists);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return numerals;
    return numerals.filter((n) =>
      n.numeral.toLowerCase().includes(q) || (n.label || '').toLowerCase().includes(q),
    );
  }, [numerals, search]);

  // Phase-1 figure-scope — partition into TWO groups now:
  //   A: placed on the active figure (drafter's working set)
  //   B: in registry, not placed anywhere yet (the holding pen
  //      for analyze-extracted numerals waiting to be claimed)
  // Group C ("placed only on other figures") was retired — the
  // panel is figure-scoped so other-figure placements aren't
  // surfaced here. Switch to the other figure to see them.
  const { groupA, groupB } = useMemo(() => {
    const a: DrawingNumeralRow[] = [];
    const b: DrawingNumeralRow[] = [];
    for (const n of filtered) {
      const placedOn = placementMap.get(n.id);
      if (!placedOn || placedOn.size === 0) {
        b.push(n);
      } else if (activeFigureId && placedOn.has(activeFigureId)) {
        a.push(n);
      }
      // numerals placed only on other figures are dropped from
      // the active rendering — figure-scope contract.
    }
    return { groupA: a, groupB: b };
  }, [filtered, placementMap, activeFigureId]);

  const onSavePatch = useCallback(async (
    n: DrawingNumeralRow,
    body: NumeralPatchBody,
  ) => {
    setActionError(null);
    try {
      await patchDrawingNumeral(matterId, n.id, body);
      await refreshLists(matterId);
      setEditingId(null);
    } catch (e: unknown) {
      setActionError(extractMessage(e, 'Save failed.'));
    }
  }, [matterId, refreshLists]);

  const onDelete = useCallback(async (n: DrawingNumeralRow) => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const ok = window.confirm(
        `Delete numeral ${n.numeral}? Existing canvas chips for this numeral will become orphaned.`,
      );
      if (!ok) return;
    }
    setActionError(null);
    try {
      await deleteDrawingNumeral(matterId, n.id);
      await refreshLists(matterId);
      setEditingId(null);
    } catch (e: unknown) {
      setActionError(extractMessage(e, 'Delete failed.'));
    }
  }, [matterId, refreshLists]);

  const onCreate = useCallback(async (body: NumeralCreateBody) => {
    setActionError(null);
    try {
      await createDrawingNumeral(matterId, body);
      await refreshLists(matterId);
      setAdding(false);
    } catch (e: unknown) {
      setActionError(extractMessage(e, 'Create failed.'));
    }
  }, [matterId, refreshLists]);

  const showSuggestionStripe = numerals.length === 0 && primedNumerals.length > 0;

  return (
    <div className="flex h-full flex-col">
      <NumeralsHeader
        onAdd={() => { setAdding(true); setEditingId(null); }}
        search={search}
        onSearchChange={setSearch}
      />
      {actionError && (
        <p
          className="border-b border-red-200 bg-red-50 px-3 py-1.5 text-[11px] text-red-700"
          data-testid="numerals-action-error"
        >
          {actionError}
        </p>
      )}
      {adding && (
        <NumeralEditPanel
          mode="create"
          claimTermOptions={claimTermOptions}
          onCancel={() => setAdding(false)}
          onCreate={onCreate}
        />
      )}
      {!adding && !activeFigureId && numerals.length > 0 && (
        // BUG-030 — figure-scoped grouping needs an active figure
        // to make sense (Group A is "placed on THIS figure").
        // Surface a clear hint so the empty list reads as
        // "select a figure first" rather than "no data".
        <EmptyState
          icon={<Hash className="h-8 w-8 text-zinc-300" />}
          title="Select a figure"
          body="Pick a figure from the gallery — the panel groups numerals by what's already placed on the active figure."
        />
      )}
      {!adding && numerals.length === 0 && !showSuggestionStripe && (
        <EmptyState
          icon={<Hash className="h-8 w-8 text-zinc-300" />}
          title="No numerals on this figure yet"
          body="Click Analyze on this figure to extract numerals from the image, or type a number on the canvas with the text tool."
        />
      )}
      {showSuggestionStripe && (
        <ul className="divide-y divide-zinc-100" role="list" data-testid="numerals-list-primed">
          <li className="border-b border-[#002FA7]/20 bg-[#002FA7]/5 px-3 py-2 text-[11px] text-[#002FA7]">
            Suggestions from priming — Auto-map (Phase 8) confirms.
          </li>
          {primedNumerals.map((n) => (
            <li key={n} className="px-3 py-2.5">
              <span className="font-mono text-sm font-semibold text-zinc-900">{n}</span>
            </li>
          ))}
        </ul>
      )}
      {activeFigureId && numerals.length > 0 && filtered.length === 0 && (
        <p className="px-3 py-6 text-center text-xs text-zinc-500">
          No numerals match "{search}".
        </p>
      )}
      {activeFigureId && filtered.length > 0 && (
        <div className="flex flex-col" data-testid="numerals-list">
          <NumeralGroup
            kind="A"
            label="On this figure"
            rows={groupA}
            editingId={editingId}
            onEdit={(id) => { setEditingId(id); setAdding(false); }}
            onCancelEdit={() => setEditingId(null)}
            onSavePatch={onSavePatch}
            onDelete={onDelete}
            claimTermOptions={claimTermOptions}
            // Phase 1 — figure-scope panel; the place-pill
            // affordance comes out across both groups. The canvas
            // # tool itself is removed in Phase 3, but the pill
            // is dropped now so the rail stops surfacing an
            // affordance that's about to disappear.
            onPlace={null}
            disabledReason={undefined}
            placedHere
            armedNumeralId={armedNumeralId}
            figures={figures}
            placementMap={placementMap}
            mappedNumeralIds={mappedNumeralIds}
            activeFigureId={activeFigureId}
            collapsible={false}
            expanded={true}
            onToggleExpanded={() => undefined}
          />
          <NumeralGroup
            kind="B"
            label="Extracted but not yet on a figure"
            rows={groupB}
            editingId={editingId}
            onEdit={(id) => { setEditingId(id); setAdding(false); }}
            onCancelEdit={() => setEditingId(null)}
            onSavePatch={onSavePatch}
            onDelete={onDelete}
            claimTermOptions={claimTermOptions}
            mappedNumeralIds={mappedNumeralIds}
            onPlace={null}
            placedHere={false}
            disabledReason={undefined}
            armedNumeralId={armedNumeralId}
            figures={figures}
            placementMap={placementMap}
            activeFigureId={activeFigureId}
            collapsible={false}
            expanded={true}
            onToggleExpanded={() => undefined}
          />
        </div>
      )}
    </div>
  );
}

// BUG-030 — section wrapper for one of the three figure-scoped
// groups. Header carries a count + (for Group C) a collapse
// chevron. ``onPlace`` is null on Group C since "place →" only
// makes sense for numerals the drafter actually wants on the
// active figure.
function NumeralGroup({
  kind, label, rows, editingId, onEdit, onCancelEdit, onSavePatch, onDelete,
  claimTermOptions, onPlace, disabledReason, placedHere, armedNumeralId,
  figures, placementMap, mappedNumeralIds,
  activeFigureId, collapsible, expanded, onToggleExpanded,
}: {
  kind: 'A' | 'B' | 'C';
  label: string;
  rows: DrawingNumeralRow[];
  editingId: string | null;
  onEdit: (id: string) => void;
  onCancelEdit: () => void;
  onSavePatch: (n: DrawingNumeralRow, body: NumeralPatchBody) => Promise<void>;
  onDelete: (n: DrawingNumeralRow) => Promise<void>;
  claimTermOptions: { claimId: string; claimNumber: number; term: string }[];
  onPlace: ((numeralId: string) => void) | null;
  /** BUG-035 — when ``onPlace`` is null but a place affordance
   * SHOULD appear (e.g. Group B on a frozen figure), pass the
   * reason so NumeralRow can render the pill disabled with an
   * explanatory tooltip instead of suppressing the button. */
  disabledReason?: string;
  /** BUG-030 — true for Group A; renders the "placed ✓" indicator
   * instead of the "place →" pill. */
  placedHere: boolean;
  armedNumeralId: string | null;
  figures: DrawingFigureSummary[];
  placementMap: Map<string, Set<string>>;
  /** Set of numeral ids that appear as ``drawing_numeral_id`` on
   *  some term-mapping row. Drives the 'mapped' middle state of
   *  the NumeralRow badge — auto-map writes only the term-mapping
   *  side for medium/low confidence resolutions, so this is what
   *  surfaces them in the Numerals tab. */
  mappedNumeralIds: Set<string>;
  activeFigureId: string | null;
  collapsible: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  if (rows.length === 0) return null;
  const figureNumberById = new Map(figures.map((f) => [f.id, f.sequence_num]));
  return (
    <section data-testid={`numerals-group-${kind.toLowerCase()}`}>
      <header
        className="sticky top-0 z-[1] flex items-center justify-between border-y border-zinc-200 bg-zinc-50 px-3 py-1.5"
      >
        <button
          type="button"
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-600 hover:text-zinc-900"
          onClick={onToggleExpanded}
          disabled={!collapsible}
          data-testid={`numerals-group-${kind.toLowerCase()}-toggle`}
        >
          {collapsible && (
            <span aria-hidden>{expanded ? '▼' : '▶'}</span>
          )}
          <span>{kind}. {label}</span>
        </button>
        <span className="text-[10px] font-medium text-zinc-500">{rows.length}</span>
      </header>
      {expanded && (
        <ul className="divide-y divide-zinc-100" role="list">
          {rows.map((n) => (
            editingId === n.id ? (
              <li key={n.id} data-testid={`numerals-row-${n.id}`}>
                <NumeralEditPanel
                  mode="edit"
                  numeral={n}
                  claimTermOptions={claimTermOptions}
                  onCancel={onCancelEdit}
                  onSavePatch={(body) => onSavePatch(n, body)}
                  onDelete={() => onDelete(n)}
                />
              </li>
            ) : (
              <NumeralRow
                key={n.id}
                numeral={n}
                onEdit={() => onEdit(n.id)}
                onPlace={onPlace ? () => onPlace(n.id) : null}
                placedHere={placedHere}
                disabledReason={disabledReason}
                isArmed={armedNumeralId === n.id}
                isMappedByTerm={mappedNumeralIds.has(n.id)}
                placedFigures={Array.from(placementMap.get(n.id) || [])
                  .map((fid) => ({ id: fid, num: figureNumberById.get(fid) || 0 }))
                  .filter((f) => f.num > 0 && f.id !== activeFigureId)
                  .sort((a, b) => a.num - b.num)
                  .map((f) => f.num)}
              />
            )
          ))}
        </ul>
      )}
    </section>
  );
}

interface NumeralRowProps {
  numeral: DrawingNumeralRow;
  onEdit: () => void;
  /** BUG-030 — when non-null, renders the "place →" pill that
   * arms this numeral for one-shot canvas placement. Null on
   * Group A (already placed here) and Group C (placed-only-on-
   * other-figures) since neither needs the arm-and-place flow. */
  onPlace: (() => void) | null;
  /** BUG-030 — Group A row: already on the active figure. Renders
   * a "placed ✓" indicator in place of the pill. */
  placedHere: boolean;
  /** BUG-035 — when set, the place pill renders disabled with this
   * reason as the tooltip. Used when ``onPlace`` is null only
   * because the active figure is in a state that rejects clicks
   * (frozen, etc.) — drafter still sees the affordance, gets the
   * reason, no dead-end armed state. */
  disabledReason?: string;
  /** BUG-030 — true when the panel just armed this row; the pill
   * gets a blue ring + label flips to "Click on canvas …". */
  isArmed: boolean;
  /** BUG-030 — list of figure_numbers where this numeral already
   * has a chip, EXCLUDING the active figure. Surfaces below the
   * label as "FIG. 2 · FIG. 5". */
  placedFigures: number[];
  /** Auto-map signal: when true, this numeral.id appears as the
   *  drawing_numeral_id on at least one drawing_term_mappings
   *  row. Drives the 'mapped' middle state of the status badge
   *  for the case where auto-map produced a medium/low resolution
   *  (which intentionally does NOT write bound_claim_id to leave
   *  drafter confirmation in the loop). */
  isMappedByTerm: boolean;
}

/** Per-numeral row. Numeral square · label · status pill ·
 * [place → | placed ✓] · [edit]. */
function NumeralRow({
  numeral, onEdit, onPlace, placedHere, disabledReason,
  isArmed, placedFigures, isMappedByTerm,
}: NumeralRowProps) {
  // Three-state status:
  //   linked   — bound_claim_id is set on the drawing_numerals row
  //              (auto-map high-confidence OR drafter manual bind).
  //   mapped   — no bound_claim_id, but a drawing_term_mappings row
  //              points at this numeral (medium/low auto-map
  //              resolution; drafter confirmation pending).
  //   unmapped — neither.
  // The previous binary "bound_claim_id ? linked : unmapped" hid
  // the medium/low auto-map outcomes — the Terms tab showed
  // "mapped" but this tab said "unmapped" and drafters perceived
  // auto-map as broken.
  const status: 'linked' | 'mapped' | 'unmapped' =
    numeral.bound_claim_id
      ? 'linked'
      : isMappedByTerm
        ? 'mapped'
        : 'unmapped';
  // PD-005 — drag-from-rail flow. The row is draggable from any
  // hit-area inside the chrome. dataTransfer carries the registry
  // numeral id + the cached numeral/label, in a dedicated MIME so
  // the Canvas drop handler can pick it up alongside the existing
  // term-glossary drag without collisions. This is the only way to
  // PLACE a numeral on a figure when auto-map returns 0; without
  // it, Analyze-extracted numerals had no manual placement path
  // and the drafter was stuck if Auto-map didn't fire.
  const [dragging, setDragging] = useState(false);
  const onRowDragStart = useCallback(
    (e: React.DragEvent<HTMLLIElement>) => {
      if (numeral.is_stale) {
        // Stale rows can't place — the registry row needs to be
        // re-bound first via the Edit affordance. Cancel the drag
        // before dataTransfer is populated so the cursor stays in
        // the no-drop state.
        e.preventDefault();
        return;
      }
      const payload: DraggedNumeralPayload = {
        numeralId: numeral.id,
        numeral: numeral.numeral,
        label: numeral.label || '',
      };
      e.dataTransfer.setData(
        DRAWING_NUMERAL_DRAG_TYPE, JSON.stringify(payload),
      );
      // text/plain fallback so a stray drop on a contenteditable
      // surface elsewhere produces sensible content.
      e.dataTransfer.setData("text/plain", numeral.numeral);
      e.dataTransfer.effectAllowed = "copy";
      setDragging(true);
    },
    [numeral.id, numeral.is_stale, numeral.numeral, numeral.label],
  );
  const onRowDragEnd = useCallback(() => setDragging(false), []);

  return (
    <li
      data-testid={`numerals-row-${numeral.id}`}
      draggable={!numeral.is_stale}
      onDragStart={onRowDragStart}
      onDragEnd={onRowDragEnd}
      title={
        numeral.is_stale
          ? "Resolve the stale binding before placing this numeral on a figure."
          : "Drag onto the figure to place this numeral as a chip."
      }
    >
      <div className={[
        'flex items-start gap-3 px-3 py-2.5',
        isArmed ? 'bg-[#002FA7]/5 ring-1 ring-inset ring-[#002FA7]/30' : '',
        !numeral.is_stale
          ? 'cursor-grab active:cursor-grabbing'
          : 'cursor-not-allowed',
        dragging ? 'bg-[#002FA7]/5 ring-1 ring-[#002FA7]/30' : '',
      ].filter(Boolean).join(' ')}>
        <NumeralSquareTag value={numeral.numeral} stale={numeral.is_stale} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-medium text-zinc-900">
              {numeral.label || <em className="text-zinc-400">no label</em>}
            </span>
            <NumeralStatusBadge status={status} stale={numeral.is_stale} />
          </div>
          {numeral.bound_claim_term && (
            <p className="mt-0.5 text-[11px] italic text-zinc-500">
              ↪ {numeral.bound_claim_term}
            </p>
          )}
          {placedFigures.length > 0 && (
            <p
              className="mt-0.5 truncate text-[10px] font-mono text-zinc-500"
              title={`Already placed on ${placedFigures.map((n) => `FIG. ${n}`).join(', ')}`}
            >
              {placedFigures.map((n) => `FIG. ${n}`).join(' · ')}
            </p>
          )}
        </div>
        {/* Phase 1 figure-scope — single "on this figure"
            indicator. The place-pill + disabled-frozen variant
            were retired; the rail is read-only over the figure's
            current numerals. New numerals come in via Analyze
            extraction or auto-map's text-annotation pass (Phase
            4). The # canvas tool itself is removed in Phase 3. */}
        {placedHere && (
          <span
            data-testid={`numerals-placed-${numeral.id}`}
            className="ml-1 inline-flex h-6 items-center gap-1 rounded-sm border border-green-200 bg-green-50 px-2 text-[10px] font-medium text-green-700"
            title="This numeral has a chip on the active figure."
          >
            <Check size={10} aria-hidden="true" />
            on this figure
          </span>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="ml-1 inline-flex h-6 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[10px] font-medium text-zinc-700 hover:bg-zinc-50"
          data-testid={`numerals-edit-${numeral.id}`}
          title="Edit label and claim-term binding"
          aria-label={`Edit numeral ${numeral.numeral}`}
        >
          <Pencil className="h-3 w-3" />
          edit
        </button>
      </div>
    </li>
  );
}

function NumeralStatusBadge({
  status, stale,
}: {
  status: 'linked' | 'mapped' | 'unmapped';
  stale: boolean;
}) {
  if (stale) {
    return (
      <span
        className="inline-flex items-center rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
        title="A claim edit may have invalidated this numeral. Click edit to re-bind."
      >
        resolve
      </span>
    );
  }
  if (status === 'linked') {
    return (
      <span
        className="inline-flex items-center rounded-sm bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 ring-1 ring-green-200"
        title="Bound to a claim term."
      >
        ✓ linked
      </span>
    );
  }
  if (status === 'mapped') {
    return (
      <span
        className="inline-flex items-center rounded-sm bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-blue-200"
        title="Auto-map proposed a claim-term link — click edit to confirm or change."
      >
        → mapped
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-sm bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600 ring-1 ring-zinc-200"
      title="No claim-term binding yet — click edit to add one."
    >
      unmapped
    </span>
  );
}

interface NumeralEditPanelProps {
  mode: 'edit' | 'create';
  numeral?: DrawingNumeralRow;
  claimTermOptions: { claimId: string; claimNumber: number; term: string }[];
  onCancel: () => void;
  onSavePatch?: (body: NumeralPatchBody) => Promise<void> | void;
  onCreate?: (body: NumeralCreateBody) => Promise<void> | void;
  onDelete?: () => Promise<void> | void;
}

/** Inline edit / create panel. Binds a single label + at-most-one
 * claim term. Two persistence paths:
 *   create — POST /drawing/numerals
 *   edit   — PATCH /drawing/numerals/{id}; ``unbind: true`` clears
 *            both binding columns atomically when the drafter
 *            picks the 'unmapped' radio. */
function NumeralEditPanel({
  mode, numeral, claimTermOptions, onCancel, onSavePatch, onCreate, onDelete,
}: NumeralEditPanelProps) {
  const [num, setNum] = useState(mode === 'edit' ? numeral?.numeral || '' : '');
  const [label, setLabel] = useState(numeral?.label || '');
  const [boundClaimId, setBoundClaimId] = useState<string | null>(
    numeral?.bound_claim_id ?? null,
  );
  const [boundClaimTerm, setBoundClaimTerm] = useState<string | null>(
    numeral?.bound_claim_term ?? null,
  );
  const [busy, setBusy] = useState(false);

  const selectedKey = boundClaimId && boundClaimTerm
    ? `${boundClaimId}|${boundClaimTerm.toLowerCase()}`
    : 'unmapped';

  const onSelectClaimTerm = (claimId: string, term: string) => {
    setBoundClaimId(claimId);
    setBoundClaimTerm(term);
  };
  const onSelectUnmapped = () => {
    setBoundClaimId(null);
    setBoundClaimTerm(null);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'create' && onCreate) {
        await onCreate({
          numeral: num.trim(),
          label: label.trim(),
          bound_claim_id: boundClaimId || null,
          bound_claim_term: boundClaimTerm || null,
        });
      } else if (mode === 'edit' && onSavePatch) {
        if (!boundClaimId || !boundClaimTerm) {
          await onSavePatch({ label: label.trim(), unbind: true });
        } else {
          await onSavePatch({
            label: label.trim(),
            bound_claim_id: boundClaimId,
            bound_claim_term: boundClaimTerm,
          });
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = mode === 'create'
    ? num.trim() !== '' && label.trim() !== '' && !busy
    : label.trim() !== '' && !busy;

  return (
    <div
      className="border-y border-[#002FA7]/30 bg-[#002FA7]/5 px-3 py-3"
      data-testid="numerals-edit-panel"
    >
      <div className="mb-2 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
        {mode === 'create' ? 'Add numeral' : `Numeral ${numeral?.numeral} — ${numeral?.label || ''}`}
      </div>
      {mode === 'create' && (
        <label className="mb-2 block text-[11px] font-medium text-zinc-700">
          Numeral
          <input
            type="text"
            value={num}
            onChange={(e) => setNum(e.target.value)}
            placeholder="e.g. 706"
            className="mt-0.5 h-7 w-full rounded-sm border border-zinc-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            data-testid="numerals-edit-numeral"
          />
        </label>
      )}
      <label className="mb-2 block text-[11px] font-medium text-zinc-700">
        Label
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. cargo tank"
          className="mt-0.5 h-7 w-full rounded-sm border border-zinc-300 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          data-testid="numerals-edit-label"
        />
      </label>
      <fieldset className="mb-2">
        <legend className="text-[11px] font-medium text-zinc-700">
          Bind to claim term
        </legend>
        {claimTermOptions.length === 0 ? (
          <p className="mt-1 text-[11px] italic text-zinc-500">
            No claim terms available.
          </p>
        ) : (
          <div className="mt-1 max-h-40 overflow-y-auto rounded-sm border border-zinc-200 bg-white p-1.5">
            <label className="flex cursor-pointer items-center gap-1.5 px-1 py-0.5 text-[11px] hover:bg-zinc-50">
              <input
                type="radio"
                name={`numerals-claim-term-${numeral?.id || 'create'}`}
                value="unmapped"
                checked={selectedKey === 'unmapped'}
                onChange={onSelectUnmapped}
                data-testid="numerals-edit-unmapped"
              />
              <span className="text-zinc-600">unmapped</span>
            </label>
            {claimTermOptions.map((opt) => {
              const key = `${opt.claimId}|${opt.term.toLowerCase()}`;
              return (
                <label
                  key={key}
                  className="flex cursor-pointer items-center gap-1.5 px-1 py-0.5 text-[11px] hover:bg-zinc-50"
                >
                  <input
                    type="radio"
                    name={`numerals-claim-term-${numeral?.id || 'create'}`}
                    value={key}
                    checked={selectedKey === key}
                    onChange={() => onSelectClaimTerm(opt.claimId, opt.term)}
                    data-testid={`numerals-edit-radio-${opt.claimId}-${opt.term}`}
                  />
                  <span className="text-zinc-900">{opt.term}</span>
                  <span className="text-zinc-400">(claim {opt.claimNumber})</span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
      <div className="flex items-center justify-end gap-2">
        {mode === 'edit' && onDelete && (
          <button
            type="button"
            onClick={() => { void onDelete(); }}
            disabled={busy}
            className="mr-auto inline-flex h-7 items-center gap-1 rounded-sm border border-red-300 bg-white px-2 text-[11px] font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            data-testid="numerals-edit-delete"
          >
            Delete numeral
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="inline-flex h-7 items-center rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="numerals-edit-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { void submit(); }}
          disabled={!canSubmit}
          className="inline-flex h-7 items-center rounded-sm bg-[#002FA7] px-2 text-[11px] font-medium text-white hover:bg-[#002277] disabled:opacity-50"
          data-testid="numerals-edit-save"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function extractMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail === 'object') {
    const d = detail as Record<string, unknown>;
    if (typeof d.detail === 'string' && d.detail) return d.detail;
    if (typeof d.message === 'string' && d.message) return d.message;
  }
  const msg = (e as { message?: string })?.message;
  if (typeof msg === 'string' && msg) return msg;
  return fallback;
}

function NumeralsHeader({
  onAdd, search, onSearchChange,
}: {
  onAdd: () => void;
  search: string;
  onSearchChange: (v: string) => void;
}) {
  return (
    <div className="sticky top-0 z-10 flex flex-col gap-2 border-b border-zinc-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1.5 h-3.5 w-3.5 text-zinc-400" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search numeral or term…"
            className="h-7 w-full rounded-sm border border-zinc-300 bg-white pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
            data-testid="numerals-search"
          />
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-7 items-center gap-1 rounded-sm border border-[#002FA7] bg-[#002FA7] px-2 text-[11px] font-medium text-white hover:bg-[#002277]"
          data-testid="numerals-new"
          title="Add a numeral to the registry."
        >
          <Plus className="h-3 w-3" />
          Add numeral
        </button>
      </div>
    </div>
  );
}

function NumeralSquareTag({ value, stale }: { value: string; stale: boolean }) {
  return (
    <span
      className={[
        'inline-flex h-6 min-w-[1.75rem] items-center justify-center rounded-sm font-mono text-[11px] font-semibold',
        stale
          ? 'bg-zinc-100 text-zinc-500 ring-1 ring-zinc-300'
          : 'bg-[#002FA7] text-white',
      ].join(' ')}
    >
      {value}
    </span>
  );
}

function PillKindBadge({
  kind, stale, onPlaceClick,
}: {
  kind: 'linked' | 'place' | 'resolve' | null;
  stale: boolean;
  onPlaceClick?: () => void;
}) {
  if (stale) {
    return (
      <span
        className="inline-flex items-center rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200"
        title="A claim edit may have invalidated this numeral. Run Verify when Phase 8 lands."
      >
        stale
      </span>
    );
  }
  if (!kind) return null;
  const palette = kind === 'linked'
    ? 'bg-green-50 text-green-700 ring-1 ring-green-200'
    : kind === 'place'
      ? 'bg-zinc-100 text-zinc-600 ring-1 ring-zinc-200 hover:bg-[#002FA7] hover:text-white hover:ring-[#002FA7] cursor-pointer'
      : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
  const label = kind === 'linked' ? 'linked'
    : kind === 'place' ? 'place →' : 'resolve →';
  const tip = kind === 'linked'
    ? 'Bound to a claim term.'
    : kind === 'place'
      ? 'Click to arm canvas placement — the next click on the canvas drops a chip for this numeral.'
      : 'Placed on canvas but not bound to a claim term.';
  // BUG-020 — clicking 'place →' arms the canvas with this
  // registry numeral id. The next canvas click commits a chip
  // directly, skipping the new-vs-reuse modal. Rendered as a
  // span+role="button" rather than a real <button> because the
  // surrounding NumeralRow IS a <button> and HTML disallows
  // nested buttons.
  if (kind === 'place' && onPlaceClick) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onPlaceClick();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onPlaceClick();
          }
        }}
        className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium transition-colors ${palette}`}
        title={tip}
        data-testid="place-pill-button"
      >
        {label}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10px] font-medium ${palette}`}
      title={tip}
    >
      {label}
    </span>
  );
}

// ─── Claims tab ───────────────────────────────────────────────────

function ClaimsTab({
  matterId, termMappings, numerals, primeCandidates = [],
}: {
  matterId: string;
  termMappings: DrawingTermMappingRow[];
  numerals: DrawingNumeralRow[];
  primeCandidates?: ClaimPrimeJson['candidate_claim_elements'];
}) {
  // Slice 9E — hooks must run before any early-return path so React
  // sees the same ordering on every render. Pull the matter's claim
  // list (used to surface claim numbers in the post-mapping rollup)
  // and the modal opener up here even when this render path doesn't
  // need them — the cost is one map lookup.
  const claimsList = useAppStore((s) => s.matterState?.claims);
  const claimNumberById = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of claimsList || []) m.set(c.id, c.claim_number);
    return m;
  }, [claimsList]);
  // BUG-021 — surface a truncated claim body inside each card so
  // the panel doesn't read as just "Claim 1 · 4 of 4 terms mapped"
  // with no actual claim text. Cap at ~100 chars per the spec.
  const claimTextById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of claimsList || []) m.set(c.id, c.claim_text || '');
    return m;
  }, [claimsList]);
  const openClaimEditModal = useDrawingStore((s) => s.openClaimEditModal);
  const candidates = primeCandidates || [];
  // BUG-021 — only fall through to the empty-state when there are
  // also no frozen claims. With at least one frozen claim, the
  // panel renders one card per claim (with truncated text + the
  // edit/flag icons) even before any term mapping exists.
  const haveFrozenClaims = (claimsList || []).length > 0;
  if (termMappings.length === 0 && candidates.length === 0 && !haveFrozenClaims) {
    return (
      <EmptyState
        icon={<FileText className="h-8 w-8 text-zinc-300" />}
        title="No claim mappings yet"
        body="After a figure is primed, candidate claim elements appear here with per-claim rollups. Phase 6 wires per-term mapping; Phase 8 wires Auto-map for rollups."
      />
    );
  }
  if (termMappings.length === 0 && candidates.length > 0) {
    // Phase 5 priming output only — group candidates by claim_id.
    const byClaim = new Map<string, NonNullable<ClaimPrimeJson['candidate_claim_elements']>>();
    for (const c of candidates) {
      const key = c?.claim_id || '?';
      const arr = byClaim.get(key) || [];
      arr.push(c);
      byClaim.set(key, arr);
    }
    return (
      <ul className="divide-y divide-zinc-100" role="list" data-testid="claims-list-primed">
        <li className="border-b border-[#002FA7]/20 bg-[#002FA7]/5 px-3 py-2 text-[11px] text-[#002FA7]">
          Candidate elements from priming — Auto-map (Phase 8) confirms.
        </li>
        {Array.from(byClaim.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([claimId, rows]) => (
          <li key={claimId} className="px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-zinc-900">
                {claimNumberById.get(claimId) !== undefined
                  ? `Claim ${claimNumberById.get(claimId)}`
                  : `Claim ${claimId.slice(0, 8)}`}
              </span>
              <span className="text-[11px] font-medium text-zinc-500">
                {rows.length} candidate{rows.length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="mt-1 space-y-0.5">
              {rows.map((r, i) => (
                <li key={`${r?.element_name}-${i}`} className="text-[11px] text-zinc-600">
                  {r?.element_name || '(no element)'}
                  {r?.confidence && (
                    <ConfidenceBadge confidence={String(r.confidence)} />
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    );
  }
  // BUG-021 — group mappings by claim id then union with the
  // frozen claim list so claims with zero term-mappings still
  // get a card. Sort the resulting list by claim_number ascending
  // (UUID alphabetical was the prior behaviour and produced
  // arbitrary order in the rail).
  //
  // Claims-tab redesign — single unified per-claim list.
  // Previously the tab rendered TWO sections (UnmappedTermsPanel
  // at the top + per-claim cards below); the unmapped panel
  // labelled each TERM card with its parent claim, which made
  // claims with multiple unmapped terms look like duplicate
  // claim cards. Each frozen claim now gets exactly one card;
  // the unmapped-term picker UI moves INSIDE the per-claim card
  // so the drafter sees "Claim 4 has 3 unmapped terms; pick a
  // numeral for each" without a separate panel.
  const byClaim = new Map<string, DrawingTermMappingRow[]>();
  for (const r of termMappings) {
    const arr = byClaim.get(r.claim_id) || [];
    arr.push(r);
    byClaim.set(r.claim_id, arr);
  }
  for (const c of claimsList || []) {
    if (!byClaim.has(c.id)) byClaim.set(c.id, []);
  }
  const groups = Array.from(byClaim.entries()).sort(([a], [b]) => {
    const na = claimNumberById.get(a) ?? Number.MAX_SAFE_INTEGER;
    const nb = claimNumberById.get(b) ?? Number.MAX_SAFE_INTEGER;
    if (na !== nb) return na - nb;
    return a.localeCompare(b);
  });
  // Per-claim type / parent lookup so the rendered card can
  // distinguish independent claims from dependents and
  // (visually) indent the dependent ladder.
  const claimMetaById = new Map<
    string, { type: string; parent: number | null }
  >();
  for (const c of (claimsList || [])) {
    claimMetaById.set(c.id, {
      type: c.claim_type || "",
      parent: c.parent_claim_number ?? null,
    });
  }
  return (
    <div className="flex flex-col">
      <ul className="divide-y divide-zinc-100" role="list" data-testid="claims-list">
      {groups.map(([claimId, rows]) => {
        const mappedRows = rows.filter((r) => r.drawing_numeral_id !== null);
        const unmappedRows = rows.filter(
          (r) => r.drawing_numeral_id === null
            && r.claim_term !== "unclaimed_support",
        );
        const mapped = mappedRows.length;
        const total = rows.filter(
          (r) => r.claim_term !== "unclaimed_support",
        ).length;
        const claimNumber = claimNumberById.get(claimId);
        const claimMeta = claimMetaById.get(claimId);
        const isDependent = (claimMeta?.type || "") === "dependent";
        const headerLabel = claimNumber !== undefined
          ? (
            isDependent && claimMeta?.parent
              ? `Claim ${claimNumber} (of claim ${claimMeta.parent})`
              : (claimMeta?.type === "independent"
                ? `Claim ${claimNumber} (independent)`
                : `Claim ${claimNumber}`)
          )
          : `Claim ${claimId.slice(0, 8)}`;
        const bodyText = claimTextById.get(claimId) || '';
        const truncated = bodyText.length > 100
          ? bodyText.slice(0, 100).trimEnd() + '…'
          : bodyText;
        return (
          <li
            key={claimId}
            className={[
              "px-3 py-2.5",
              isDependent ? "pl-6" : "",
            ].join(" ")}
            data-testid={`claim-card-${claimId}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-zinc-900">
                {headerLabel}
              </span>
              <div className="flex items-center gap-1">
                {total > 0 ? (
                  mapped === total ? (
                    <span
                      className="mr-1 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700"
                      data-testid={`claim-status-${claimId}`}
                    >
                      <Check className="h-3 w-3" />
                      {mapped} mapped
                    </span>
                  ) : (
                    <span
                      className="mr-1 text-[11px] font-medium text-amber-700"
                      data-testid={`claim-status-${claimId}`}
                    >
                      {mapped} of {total} mapped
                    </span>
                  )
                ) : (
                  <span
                    className="mr-1 text-[11px] text-zinc-400"
                    data-testid={`claim-status-${claimId}`}
                  >
                    — no terms
                  </span>
                )}
                {claimNumber !== undefined && (
                  <>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                      onClick={() => openClaimEditModal(claimId)}
                      data-testid={`claim-edit-open-${claimId}`}
                      aria-label={`Edit ${headerLabel}`}
                      title="Edit claim text"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50"
                      onClick={() => openClaimEditModal(claimId)}
                      data-testid={`claim-flag-open-${claimId}`}
                      aria-label={`Flag ${headerLabel}`}
                      title="Flag claim for revision"
                    >
                      <Flag className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
            {bodyText && (
              <p
                className="mt-1 text-[11px] leading-snug text-zinc-600"
                title={bodyText}
                data-testid={`claim-body-${claimId}`}
              >
                {truncated}
              </p>
            )}
            {/* Mapped numeral chips for this claim — inline
                summary so the drafter sees the per-claim
                bindings without opening another tab. */}
            {mappedRows.length > 0 && (
              <div
                className="mt-1.5 flex flex-wrap gap-1"
                data-testid={`claim-mapped-chips-${claimId}`}
              >
                {mappedRows.map((m) => {
                  const n = numerals.find(
                    (x) => x.id === m.drawing_numeral_id,
                  );
                  return (
                    <span
                      key={m.id}
                      className="inline-flex items-center gap-1 rounded-sm border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] text-zinc-700"
                      title={`${m.claim_term} → ${n?.numeral ?? "?"} ${n?.label ?? ""}`}
                    >
                      <code className="font-mono">[{n?.numeral ?? "?"}]</code>
                      <span className="text-zinc-500">{m.claim_term}</span>
                    </span>
                  );
                })}
              </div>
            )}
            {/* Per-claim inline picker for any unmapped terms.
                PD-002 had this as a separate panel above; nesting
                it inside the claim card keeps the visual hierarchy
                obvious (drafter sees "Claim 4 has 3 unmapped
                terms" rather than 3 floating cards labelled
                "Claim 4"). */}
            {unmappedRows.length > 0 && (
              <ClaimUnmappedTermsBlock
                matterId={matterId}
                claimId={claimId}
                unmappedRows={unmappedRows}
                numerals={numerals}
              />
            )}
          </li>
        );
      })}
      </ul>
    </div>
  );
}


function ClaimUnmappedTermsBlock({
  matterId, claimId, unmappedRows, numerals,
}: {
  matterId: string;
  claimId: string;
  unmappedRows: DrawingTermMappingRow[];
  numerals: DrawingNumeralRow[];
}) {
  // PD-002 — per-claim picker. Lives inside the parent claim
  // card so the visual hierarchy makes it obvious which claim
  // the term belongs to. Was previously a top-of-panel
  // "UnmappedTermsPanel" component that labelled each term
  // card with "Claim N" — drafters reported that arrangement
  // looked like the same claim appeared multiple times when
  // claim N had multiple unmapped terms.
  const linkTermToNumeral = useDrawingStore((s) => s.linkTermToNumeral);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const handlePick = useCallback(
    async (termMappingId: string, numeralId: string) => {
      setPendingId(termMappingId);
      try {
        await linkTermToNumeral(matterId, termMappingId, numeralId);
      } finally {
        setPendingId(null);
      }
    },
    [matterId, linkTermToNumeral],
  );
  return (
    <section
      className="mt-2 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5"
      data-testid={`claim-unmapped-block-${claimId}`}
    >
      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-amber-800">
        <AlertTriangle className="h-3 w-3" />
        {unmappedRows.length} term{unmappedRows.length === 1 ? "" : "s"} need
        {unmappedRows.length === 1 ? "s" : ""} a numeral
      </div>
      <ul className="space-y-1" role="list">
        {unmappedRows.map((m) => {
          const isPending = pendingId === m.id;
          return (
            <li
              key={m.id}
              className="rounded-sm border border-amber-200 bg-white px-2 py-1"
              data-testid={`unmapped-term-${m.id}`}
            >
              <div className="mb-0.5 text-[11px] text-zinc-700">
                <span className="font-medium">{m.claim_term}</span>
              </div>
              {numerals.length === 0 ? (
                <p className="text-[10px] italic text-zinc-500">
                  No registry numerals available yet — analyze a
                  figure first, then return here to map.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {numerals.map((n) => {
                    const isAlreadyBound = !!n.bound_claim_id;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        disabled={isPending}
                        onClick={() => handlePick(m.id, n.id)}
                        className={[
                          "inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5",
                          "text-[10px] font-medium transition-colors",
                          "disabled:cursor-not-allowed disabled:opacity-50",
                          isAlreadyBound
                            ? "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-[#002FA7] hover:text-[#002FA7]"
                            : "border-zinc-300 bg-white text-zinc-700 hover:border-[#002FA7] hover:text-[#002FA7]",
                        ].join(" ")}
                        title={
                          isAlreadyBound
                            ? `${n.numeral} is already bound to "${n.bound_claim_term}". Picking will leave the existing binding in place.`
                            : `Bind ${m.claim_term} → ${n.numeral} ${n.label}`
                        }
                        data-testid={`unmapped-pick-${m.id}-${n.id}`}
                      >
                        [{n.numeral}]
                        {n.label && (
                          <span className="font-normal">{n.label}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}


// ─── Terms tab ────────────────────────────────────────────────────

function TermsTab({
  termMappings: rawTermMappings, numerals, canvasPrimitives, activeFigureId,
}: {
  termMappings: DrawingTermMappingRow[];
  numerals: DrawingNumeralRow[];
  /** Phase 1 — fold local canvas primitives into the on-figure
   *  numeral set so a freshly-placed chip flips the Terms tab's
   *  on-figure highlight without waiting for a registry refetch.
   *  Closes the Codex-flagged grouping-lag asymmetry where the
   *  Numerals tab updated immediately but the Terms tab lagged. */
  canvasPrimitives: Record<string, CanvasPrimitive[]>;
  activeFigureId: string | null;
}) {
  // FT-011 (C3) — strip the ``unclaimed_support`` sentinel before
  // any downstream logic. The auto-mapper writes this value for
  // numerals that don't bind to a claim term (placeholder so the
  // row exists at all); surfacing it as "unclaimed_support" in the
  // Terms tab confused drafters into thinking it was a real claim
  // term they'd somehow introduced. Filtering at the entry point
  // keeps every downstream memo (on-figure split, glossary highlight,
  // counts) honest — they all see the same filtered set. The
  // sentinel still lives on the server for the auto-map pipeline.
  const termMappings = useMemo(
    () => rawTermMappings.filter((m) => m.claim_term !== 'unclaimed_support'),
    [rawTermMappings],
  );
  // BUG-032 follow-up — figure-scope the Terms tab. The set of
  // numeral ids placed on the active figure drives both the
  // ``figureMappingIds`` (term mappings whose numeral is on this
  // figure) and the on-figure-glossary highlight.
  //
  // Phase 1 — also fold in not-yet-flushed canvas primitives so
  // the highlight tracks fresh placements in lockstep with the
  // Numerals tab's groupA. Without this, a chip placed in the
  // current session would only flip the highlight after the next
  // ``refreshLists`` round-trip.
  const onFigureNumeralIds = useMemo<Set<string>>(() => {
    if (!activeFigureId) return new Set();
    const out = new Set<string>();
    for (const n of numerals) {
      const fids = Array.isArray(n.figure_ids) ? n.figure_ids : [];
      if (fids.includes(activeFigureId)) out.add(n.id);
    }
    const localPrims = canvasPrimitives[activeFigureId] || [];
    for (const p of localPrims) {
      if (p.primitive_type !== 'numeral') continue;
      if (p.drawing_numeral_id) out.add(p.drawing_numeral_id);
    }
    return out;
  }, [numerals, canvasPrimitives, activeFigureId]);

  // Slice 6G.2 — populate the glossary from the union of every
  // claim's `analysis.key_terms`. The brief calls for "the matter's
  // Stage 1 glossary"; key_terms attaches to each claim's analysis
  // (see types/index.ts) and is the closest existing source. Union
  // + case-insensitive dedupe → stable order based on first
  // appearance.
  const claims = useAppStore((s) => s.matterState?.claims);
  const glossary = useMemo<DraggedTermPayload[]>(() => {
    if (!claims) return [];
    // BUG-021 — group claim_numbers by term so the panel can show
    // "From Claim 1" / "From Claims 1, 3" instead of a UUID slice.
    // Iteration order matches first-appearance per claim_number
    // ascending so the rendered list is stable.
    const byTerm = new Map<string, {
      name: string;
      sourceClaimId: string;
      claimNumbers: number[];
    }>();
    const ordered = [...claims].sort((a, b) => a.claim_number - b.claim_number);
    for (const c of ordered) {
      const terms = c?.analysis?.key_terms || [];
      for (const t of terms) {
        if (typeof t !== 'string') continue;
        const trimmed = t.trim();
        if (!trimmed) continue;
        const key = trimmed.toLowerCase();
        const entry = byTerm.get(key);
        if (entry) {
          if (!entry.claimNumbers.includes(c.claim_number)) {
            entry.claimNumbers.push(c.claim_number);
          }
        } else {
          byTerm.set(key, {
            name: trimmed,
            sourceClaimId: c.id,
            claimNumbers: [c.claim_number],
          });
        }
      }
    }
    return Array.from(byTerm.values()).map((e) => ({
      name: e.name,
      source_claim_id: e.sourceClaimId,
      source_claim_numbers: e.claimNumbers,
    }));
  }, [claims]);

  // BUG-032 follow-up — split mappings + glossary into "on this
  // figure" vs "other" sections. The on-figure split is gated on
  // an active figure; without one, everything falls into the
  // matter-wide bucket (preserving the prior behaviour).
  const termsOnActiveFigure = useMemo<Set<string>>(() => {
    if (!activeFigureId) return new Set();
    const out = new Set<string>();
    for (const t of termMappings) {
      if (t.drawing_numeral_id && onFigureNumeralIds.has(t.drawing_numeral_id)) {
        out.add(t.claim_term.toLowerCase());
      }
    }
    return out;
  }, [termMappings, onFigureNumeralIds, activeFigureId]);

  const figureMappings = useMemo(() => {
    if (!activeFigureId) return [] as DrawingTermMappingRow[];
    return termMappings.filter((t) =>
      t.drawing_numeral_id && onFigureNumeralIds.has(t.drawing_numeral_id),
    );
  }, [termMappings, onFigureNumeralIds, activeFigureId]);
  const otherMappings = useMemo(() => {
    if (!activeFigureId) return termMappings;
    return termMappings.filter((t) =>
      !t.drawing_numeral_id || !onFigureNumeralIds.has(t.drawing_numeral_id),
    );
  }, [termMappings, onFigureNumeralIds, activeFigureId]);

  const figureGlossary = useMemo(() => {
    if (!activeFigureId) return [] as DraggedTermPayload[];
    return glossary.filter((g) => termsOnActiveFigure.has(g.name.toLowerCase()));
  }, [glossary, termsOnActiveFigure, activeFigureId]);
  const otherGlossary = useMemo(() => {
    if (!activeFigureId) return glossary;
    return glossary.filter((g) => !termsOnActiveFigure.has(g.name.toLowerCase()));
  }, [glossary, termsOnActiveFigure, activeFigureId]);

  const [otherGlossaryExpanded, setOtherGlossaryExpanded] = useState(false);
  const [otherMappingsExpanded, setOtherMappingsExpanded] = useState(false);

  const hasMappings = termMappings.length > 0;
  const hasGlossary = glossary.length > 0;

  if (!hasMappings && !hasGlossary) {
    return (
      <EmptyState
        icon={<Quote className="h-8 w-8 text-zinc-300" />}
        title="No terms yet"
        body="Glossary terms come from each claim's analysis (Stage 1 key_terms field). Once claims are frozen, drag a term onto the canvas to drop it as a text annotation."
      />
    );
  }
  return (
    <div data-testid="terms-list">
      {hasGlossary && (
        <section>
          {activeFigureId ? (
            <>
              <header className="border-b border-zinc-200 bg-zinc-50 px-3 py-1.5">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  On this figure · drag to canvas
                  <span className="ml-1 text-zinc-400">({figureGlossary.length})</span>
                </h3>
              </header>
              {figureGlossary.length === 0 ? (
                <p className="px-3 py-3 text-[11px] text-zinc-400">
                  No glossary terms placed on this figure yet.
                </p>
              ) : (
                <ul className="divide-y divide-zinc-100" role="list">
                  {figureGlossary.map((t) => (
                    <DraggableGlossaryRow key={t.name} term={t} />
                  ))}
                </ul>
              )}
              {otherGlossary.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setOtherGlossaryExpanded((v) => !v)}
                    className="flex w-full items-center justify-between border-y border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-700"
                    data-testid="terms-other-glossary-toggle"
                  >
                    <span className="flex items-center gap-1">
                      <span aria-hidden>{otherGlossaryExpanded ? '▼' : '▶'}</span>
                      Other glossary terms
                    </span>
                    <span className="text-zinc-400">{otherGlossary.length}</span>
                  </button>
                  {otherGlossaryExpanded && (
                    <ul className="divide-y divide-zinc-100" role="list">
                      {otherGlossary.map((t) => (
                        <DraggableGlossaryRow key={t.name} term={t} />
                      ))}
                    </ul>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <header className="border-b border-zinc-200 bg-zinc-50 px-3 py-1.5">
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  Glossary · drag to canvas
                </h3>
              </header>
              <ul className="divide-y divide-zinc-100" role="list">
                {glossary.map((t) => (
                  <DraggableGlossaryRow key={t.name} term={t} />
                ))}
              </ul>
            </>
          )}
        </section>
      )}
      {hasMappings && (
        <section className={hasGlossary ? 'border-t-4 border-zinc-100' : ''}>
          <header className="border-b border-zinc-200 bg-zinc-50 px-3 py-1.5">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              {activeFigureId ? 'Mappings on this figure' : 'Mapped to numerals'}
              {activeFigureId && (
                <span className="ml-1 text-zinc-400">({figureMappings.length})</span>
              )}
            </h3>
          </header>
          {activeFigureId && figureMappings.length === 0 && (
            <p className="px-3 py-3 text-[11px] text-zinc-400">
              No claim-term mappings reference numerals on this figure.
            </p>
          )}
          <ul className="divide-y divide-zinc-100" role="list">
            {(activeFigureId ? figureMappings : termMappings).map((t) => (
              <li key={t.id} className="px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-zinc-900">{t.claim_term}</span>
                  {t.drawing_numeral_id ? (
                    <span className="rounded-sm bg-[#002FA7]/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#002FA7]">
                      mapped
                    </span>
                  ) : (
                    <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">
                      unmapped
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  Source: {t.mapping_source}
                  {t.confidence && ` · confidence: ${t.confidence}`}
                </p>
              </li>
            ))}
          </ul>
          {activeFigureId && otherMappings.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setOtherMappingsExpanded((v) => !v)}
                className="flex w-full items-center justify-between border-y border-zinc-200 bg-zinc-50 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-700"
                data-testid="terms-other-mappings-toggle"
              >
                <span className="flex items-center gap-1">
                  <span aria-hidden>{otherMappingsExpanded ? '▼' : '▶'}</span>
                  Other mappings
                </span>
                <span className="text-zinc-400">{otherMappings.length}</span>
              </button>
              {otherMappingsExpanded && (
                <ul className="divide-y divide-zinc-100" role="list">
                  {otherMappings.map((t) => (
                    <li key={t.id} className="px-3 py-2.5 opacity-70">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-zinc-700">{t.claim_term}</span>
                        {t.drawing_numeral_id ? (
                          <span className="rounded-sm bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-600 ring-1 ring-zinc-200">
                            other figure
                          </span>
                        ) : (
                          <span className="rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-200">
                            unmapped
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}

function DraggableGlossaryRow({ term }: { term: DraggedTermPayload }) {
  const [dragging, setDragging] = useState(false);
  return (
    <li>
      <div
        draggable
        onDragStart={(e) => {
          // Stash the payload in dataTransfer using a dedicated
          // type so the canvas drop handler can detect drawing-term
          // drops without colliding with file uploads or other
          // browser-provided types. Also set text/plain so a stray
          // drop on a contenteditable surface elsewhere produces
          // sensible fallback content.
          e.dataTransfer.setData(DRAWING_TERM_DRAG_TYPE, JSON.stringify(term));
          e.dataTransfer.setData('text/plain', term.name);
          e.dataTransfer.effectAllowed = 'copy';
          setDragging(true);
        }}
        onDragEnd={() => setDragging(false)}
        className={[
          'flex cursor-grab items-start gap-2 px-3 py-2.5 active:cursor-grabbing',
          dragging
            ? 'bg-[#002FA7]/5 ring-1 ring-[#002FA7]/30'
            : 'hover:bg-zinc-50',
        ].join(' ')}
        data-testid={`glossary-term-${term.name}`}
      >
        <GripVertical className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <span className="text-sm text-zinc-900">{term.name}</span>
          {term.source_claim_numbers && term.source_claim_numbers.length > 0 ? (
            <p className="mt-0.5 text-[11px] text-zinc-500">
              From {term.source_claim_numbers.length === 1 ? 'Claim' : 'Claims'}{' '}
              {term.source_claim_numbers.join(', ')}
            </p>
          ) : term.source_claim_id && (
            <p className="mt-0.5 text-[11px] text-zinc-500">
              From claim {term.source_claim_id.slice(0, 8)}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── Shared empty state ───────────────────────────────────────────

function ConfidenceBadge({ confidence }: { confidence: string }) {
  const palette =
    confidence === 'high'  ? 'bg-green-50 text-green-700 ring-1 ring-green-200' :
    confidence === 'medium' ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200' :
    confidence === 'low'    ? 'bg-zinc-100 text-zinc-600' :
    'bg-zinc-100 text-zinc-600';
  return (
    <span className={`ml-1.5 inline-flex items-center rounded-sm px-1 py-0.5 text-[9px] font-medium ${palette}`}>
      {confidence}
    </span>
  );
}

function EmptyState({
  icon, title, body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="px-4 py-10 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center">
        {icon}
      </div>
      <h3 className="mb-1 text-sm font-semibold text-zinc-700">{title}</h3>
      <p className="text-xs leading-relaxed text-zinc-500">{body}</p>
    </div>
  );
}
